import { Badge, Tooltip } from "@chakra-ui/react";
import { useIsFetching } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useNavigate } from "react-router-dom";
import useEscape from "../../../hooks/useEscape";
import { matchScope } from "../../../utils/string.utils";
import AppIcon from "../../Core/AppIcon/AppIcon";
import AppText from "../../Core/AppText/AppText";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";
import ConnectionDot from "./ConnectionDot";
import CreateNewShortcut from "./CreateNewShortcut";
import GlobalSearch, { GlobalSearchHandle } from "./GlobalSearch";
import LogsShortcut from "./LogsShortcut";
import { AiAssistantTrigger, useAiAssistantEnabled } from "../AiAssistant";
import { MENU_ITEMS, MenuItem, SubMenuItem } from "../../../configs/menuItems";
import { routes } from "../../../configs/constants/routes";
import { useReportBuilderStore } from "../../../hooks/reportBuilder.hooks";
import { useDashboardSync } from "../ReportBuilder/dashboardSync";

export const SIDE_MENU_WIDTH = 110;

const subMenuLink =
	"block border-b border-[#888] last:border-b-0 text-[#888] py-5 px-[5px] hover:[&>p]:text-white";

/** Consecutive items sharing a `section` form one group, so each group can drop its last divider. */
function groupBySection(items: SubMenuItem[]) {
	const groups: { section?: string; items: SubMenuItem[] }[] = [];
	for (const item of items) {
		const last = groups[groups.length - 1];
		if (last && last.section === item.section) last.items.push(item);
		else groups.push({ section: item.section, items: [item] });
	}
	return groups;
}

interface Props {
	scopes?: string[];
	badges?: {
		[menuItem: string]: { count: number; tooltip?: string };
	};
}

const SideMenu: React.FC<Props> = ({ scopes = [], badges = {} }) => {
	const { t } = useTranslation();

	const [subMenu, setSubMenu] = useState<MenuItem>();
	const globalSearchRef = useRef<GlobalSearchHandle>(null);

	const isFetching = useIsFetching();
	const aiEnabled = useAiAssistantEnabled();
	const dashboards = useReportBuilderStore((s) => s.dashboards);
	const sharedDashboards = useReportBuilderStore((s) => s.sharedDashboards);
	const createDashboard = useReportBuilderStore((s) => s.createDashboard);
	const navigate = useNavigate();

	// Mounted here, next to the menu that lists them, so dashboards are stored
	// and pulled whichever page the user is on.
	useDashboardSync(aiEnabled);

	/** Own dashboards only; a colleague's is in the list below, fetched when opened. */
	const ownDashboards = dashboards.filter((d) => !d.owner);

	const subItemVisible = (item: SubMenuItem) =>
		(item.aiOnly ? aiEnabled : true) &&
		(!item.scope?.length || item.scope.some((s) => matchScope(s, scopes)));

	/** Scope-gated top-level items also show when an AI-only sub item is available to the user. */
	const itemVisible = (item: MenuItem) =>
		!item.scope ||
		item.scope.some((s) => matchScope(s, scopes)) ||
		!!item.items?.some((sub) => sub.aiOnly && subItemVisible(sub));

	const closeSubMenu = useCallback(() => {
		setSubMenu(undefined);
	}, []);

	function openSubMenu(item: MenuItem) {
		if (subMenu === item) {
			closeSubMenu();
		} else {
			setSubMenu(item);
		}
	}

	/** Opens a brand new, still unnamed dashboard right away - it is renamed from the dashboard page. */
	function createDashboardAndOpen() {
		createDashboard(t("reports.unnamedDashboard" as any));
		closeSubMenu();
		navigate(routes.REPORT_BUILDER);
	}

	function handleSearchClick() {
		globalSearchRef.current?.openSearch();
	}

	useEscape({ onEscape: closeSubMenu, enabled: !!subMenu });

	return (
		<div className="relative">
			<div className="flex flex-col w-[110px] h-full min-h-[700px] max-h-700:min-h-0 bg-[#333333] tracking-[-0.1px] select-none relative z-[31]">
				<div className="mx-auto mt-2.5 mb-7 flex items-center justify-center gap-1.5 max-h-700:mt-1 max-h-700:mb-6">
					<AiAssistantTrigger />
					<Tooltip label={`${t("SEARCH" as any)} (Shift+S)`} placement="right">
						<button
							type="button"
							className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-[10px] bg-[#555] opacity-60 hover:opacity-80"
							onClick={handleSearchClick}
						>
							<AppIcon color="#fff" icon="search-thin" size="20px" />
						</button>
					</Tooltip>
				</div>
				{MENU_ITEMS.map((item) => {
					if (!itemVisible(item)) {
						return null;
					}

					const badgeCount = badges[item.title]?.count || 0;
					const badgeSize = badgeCount > 9 ? "17px" : "14px";

					const innerItem = (
						<div className="relative w-[80px] flex flex-col items-center gap-1.5">
							<div>
								<AppIcon color="#fff" icon={item.icon} size="24px" />
								{badgeCount > 0 && (
									<Tooltip
										label={badges[item.title].tooltip}
										bg="white"
										textColor={"black"}
										placement="right-start"
										isDisabled={!badges[item.title].tooltip}
									>
										<Badge
											position={"absolute"}
											rounded={"full"}
											bg="red.500"
											color="white"
											w={badgeSize}
											h={badgeSize}
											fontSize={"9px"}
											lineHeight={badgeSize}
											textAlign={"center"}
											top="-1"
											right="3"
										>
											{Math.min(99, badgeCount)}
										</Badge>
									</Tooltip>
								)}
							</div>
							<div className="text-xs text-white text-center mt-0.5 max-h-700:hidden">
								{t(item.title as any)}
							</div>
						</div>
					);

					if (item.route) {
						return (
							<NavLink
								to={item.route}
								className={(navData) =>
									`block text-center mx-auto mb-[2.1rem] max-h-900:mb-7 max-h-700:mb-6 cursor-pointer ${
										navData.isActive ? "opacity-100" : "opacity-60 hover:opacity-80"
									}`
								}
								key={item.title}
								onClick={closeSubMenu}
							>
								{innerItem}
							</NavLink>
						);
					} else if (item.items) {
						return (
							<div
								className={`block text-center mx-auto mb-[2.1rem] max-h-900:mb-7 max-h-700:mb-6 cursor-pointer ${
									subMenu?.title === item.title ? "opacity-100" : "opacity-60 hover:opacity-80"
								}`}
								key={item.title}
								onClick={() => openSubMenu(item)}
							>
								{innerItem}
							</div>
						);
					} else {
						return null;
					}
				})}
				<div className="absolute bottom-4 left-0 text-center w-[110px] max-h-900:hidden">
					<div>{!!isFetching && <LoadingIndicator size={"s"} />}</div>
					<img src="/svg/origo-logo-white.svg" alt="Origo Works" className="w-[60px] mx-auto" />
				</div>
				<ConnectionDot className="absolute bottom-1.5 right-1.5 opacity-80" />
			</div>

			{subMenu && (
				<motion.div
					className="absolute top-0 left-[110px] w-[250px] bg-[#444] h-full z-30 text-white p-[15px] overflow-y-auto"
					initial={{ x: "-100%" }}
					animate={{ x: "0%" }}
					exit={{ x: "-100%" }}
					transition={{ duration: 0.125, ease: "easeOut" }}
				>
					<div
						className="absolute top-3 right-3 cursor-pointer opacity-60 hover:opacity-100"
						onClick={closeSubMenu}
					>
						<AppIcon color="#fff" icon="cross" size="16px" />
					</div>
					{groupBySection((subMenu.items || []).filter(subItemVisible)).map((group, index) => (
						<div key={group.section ?? index}>
							{group.section && (
								<div
									className={`flex items-center justify-between gap-2 px-[5px] pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#aaa] ${
										index === 0 ? "pt-7" : "pt-6"
									}`}
								>
									<span>{t(group.section as any)}</span>
									{group.items.some((item) => item.listsDashboards) && (
										<Tooltip label={t("reports.newDashboard" as any)} placement="right">
											<button
												type="button"
												className="-my-1 flex h-6 w-6 items-center justify-center rounded opacity-60 hover:bg-[#555] hover:opacity-100"
												onClick={createDashboardAndOpen}
												aria-label={t("reports.newDashboard" as any)}
											>
												<Plus className="h-4 w-4" strokeWidth={2.5} />
											</button>
										</Tooltip>
									)}
								</div>
							)}
							{group.items.map((subMenuItem) =>
								subMenuItem.listsDashboards ? (
									<React.Fragment key={subMenuItem.title}>
										{ownDashboards.length === 0 && sharedDashboards.length === 0 ? (
											<p className="px-[5px] py-4 text-[13px] leading-snug text-[#888]">
												{t("reports.noDashboards" as any)}
											</p>
										) : (
											ownDashboards.map((dashboard) => (
												<Link
													to={`${routes.REPORT_BUILDER}?report=${encodeURIComponent(dashboard.id)}`}
													className={subMenuLink}
													key={dashboard.id}
													onClick={closeSubMenu}
													title={dashboard.name}
												>
													<AppText color="dividerGray" weight="medium" className="truncate">
														{dashboard.name}
													</AppText>
												</Link>
											))
										)}
										{sharedDashboards.length > 0 && (
											<>
												<div className="px-[5px] pb-1 pt-6 text-[11px] font-semibold uppercase tracking-wide text-[#aaa]">
													{t("reports.sharedWithMe" as any)}
												</div>
												{sharedDashboards.map((dashboard) => (
													<Link
														to={`${routes.REPORT_BUILDER}?shared=${encodeURIComponent(
															dashboard.id
														)}`}
														className={subMenuLink}
														key={dashboard.id}
														onClick={closeSubMenu}
														title={
															dashboard.ownerName
																? `${dashboard.name} — delad av ${dashboard.ownerName}`
																: dashboard.name
														}
													>
														<AppText color="dividerGray" weight="medium" className="truncate">
															{dashboard.name}
														</AppText>
													</Link>
												))}
											</>
										)}
									</React.Fragment>
								) : (
									<Link
										to={subMenuItem.route}
										className={subMenuLink}
										key={subMenuItem.title}
										onClick={closeSubMenu}
									>
										<AppText color="dividerGray" weight="medium">
											{t(subMenuItem.title as any)}
										</AppText>
									</Link>
								)
							)}
						</div>
					))}
				</motion.div>
			)}
			<GlobalSearch ref={globalSearchRef} />
			<CreateNewShortcut />
			<LogsShortcut />
		</div>
	);
};

export default SideMenu;
